/**
 * Soren Past Answers View — Durable Server-Stored History.
 * Renders compact cards for answered questions stored on the server.
 * Includes "Awaiting confirmation" badge for unconfirmed actions.
 * Supports Recent (last 30 days) and Archive filters with pagination.
 */

import { useState } from 'react'
import type { PastAnswer } from './sorenAnswers'

interface SorenPastAnswersViewProps {
  answers: PastAnswer[]
  filter?: 'recent' | 'archive'
  onFilterChange?: (filter: 'recent' | 'archive') => void
  hasMore?: boolean
  onLoadMore?: () => void
  isLoading?: boolean
  onOpenAnswer: (answer: PastAnswer) => void
  onArchiveAnswer?: (id: string) => void
}

export default function SorenPastAnswersView({
  answers,
  filter: controlledFilter,
  onFilterChange,
  hasMore = false,
  onLoadMore,
  isLoading = false,
  onOpenAnswer,
  onArchiveAnswer,
}: SorenPastAnswersViewProps) {
  const [internalFilter, setInternalFilter] = useState<'recent' | 'archive'>('recent')
  const currentFilter = controlledFilter !== undefined ? controlledFilter : internalFilter

  const handleFilterClick = (newFilter: 'recent' | 'archive') => {
    if (onFilterChange) {
      onFilterChange(newFilter)
    } else {
      setInternalFilter(newFilter)
    }
  }

  const recentAnswers = answers.filter((a) => !a.isArchived)
  const archiveAnswers = answers.filter((a) => Boolean(a.isArchived))
  const displayedAnswers = currentFilter === 'recent' ? recentAnswers : archiveAnswers

  return (
    <section className="tab-view active" id="pastView" data-view="past" data-testid="soren-past-view">
      <div className="section-label">
        <span>Past answers</span>
        <span>Scrollable, recent first</span>
      </div>

      <div className="past-toolbar">
        <p className="retention-copy">
          <b>30-day working history.</b> Answers stay here for 30 days, then move to Archive automatically. They are not deleted.
        </p>
        <div className="past-filters">
          <button
            className={`past-filter ${currentFilter === 'recent' ? 'active' : ''}`}
            type="button"
            data-history-filter="recent"
            onClick={() => handleFilterClick('recent')}
          >
            Recent · 30 days
          </button>
          <button
            className={`past-filter ${currentFilter === 'archive' ? 'active' : ''}`}
            type="button"
            data-history-filter="archive"
            onClick={() => handleFilterClick('archive')}
          >
            Archive
          </button>
        </div>
      </div>

      <div className="past-scroll" id="pastScroll" data-testid="soren-past-scroll">
        {currentFilter === 'recent' && (
          <div className="past-list" id="pastList" data-history-list="recent" data-testid="soren-past-list">
            {displayedAnswers.length === 0 ? (
              <div className="past-empty" data-testid="soren-past-empty">
                <strong>No answers yet</strong>
                <p>
                  Questions you ask Soren will be saved here as compact cards. Completed answers stay in Recent for 30 days.
                </p>
              </div>
            ) : (
              displayedAnswers.map((ans) => (
                <article
                  key={ans.id}
                  className="card past-card"
                  data-history-card
                  data-testid="soren-past-card"
                >
                  <div className="past-meta">
                    <span className="past-type">{ans.type}</span>
                    {ans.awaitingConfirmation ? (
                      <span className="awaiting" data-testid="soren-awaiting-badge">
                        Awaiting confirmation
                      </span>
                    ) : (
                      <span>{ans.timeLabel}</span>
                    )}
                  </div>
                  <h3>{ans.question}</h3>
                  <p>{ans.oneLineResult}</p>
                  <div className="actions">
                    <button
                      className="btn"
                      type="button"
                      data-open-answer={ans.type.toLowerCase()}
                      onClick={() => onOpenAnswer(ans)}
                    >
                      Open answer
                    </button>
                    {onArchiveAnswer && (
                      <button
                        className="btn btn-secondary"
                        type="button"
                        onClick={() => onArchiveAnswer(ans.id)}
                        title="Move to Archive"
                      >
                        Archive
                      </button>
                    )}
                  </div>
                </article>
              ))
            )}

            {hasMore && onLoadMore && (
              <div className="past-load-more" style={{ textAlign: 'center', margin: '16px 0' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onLoadMore}
                  disabled={isLoading}
                >
                  {isLoading ? 'Loading older answers…' : 'Load older answers'}
                </button>
              </div>
            )}
          </div>
        )}

        {currentFilter === 'archive' && (
          <div className="past-list" id="archiveList" data-history-list="archive">
            {displayedAnswers.length === 0 ? (
              <div className="past-empty" data-testid="soren-archive-empty">
                <strong>Archive is empty</strong>
                <p>
                  Completed answers remain in Recent for 30 days before moving to Archive automatically.
                </p>
              </div>
            ) : (
              displayedAnswers.map((ans) => (
                <article key={ans.id} className="card past-card" data-history-card>
                  <div className="past-meta">
                    <span className="past-type">{ans.type}</span>
                    <span>Archived</span>
                  </div>
                  <h3>{ans.question}</h3>
                  <p>{ans.oneLineResult}</p>
                  <div className="actions">
                    <button
                      className="btn"
                      type="button"
                      onClick={() => onOpenAnswer(ans)}
                    >
                      Open archived answer
                    </button>
                  </div>
                </article>
              ))
            )}

            {hasMore && onLoadMore && (
              <div className="past-load-more" style={{ textAlign: 'center', margin: '16px 0' }}>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={onLoadMore}
                  disabled={isLoading}
                >
                  {isLoading ? 'Loading more archived…' : 'Load more archived'}
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  )
}
